import React, { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import {
  TextField,
  Button,
  FormHelperText,
  Grid,
  CircularProgress,
  Box,
  Card,
  CardContent,
  Typography,
} from '@mui/material';
import UploadIcon from '@mui/icons-material/Upload';
import { useNavigate } from 'react-router-dom';
import OrderServices from '../../../api/orderServices/order.index';
import instance from '../../../../axios';
import { ErrorToaster, SuccessToaster } from '../../../components/Toaster';

const CreateBlog = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [Loader, setLoader] = useState(false);
  const [uploadedImage, setUploadedImage] = useState(null);

  const handleImageUpload = (event) => {
    setLoader(true);
    const file = event.target.files[0];
    let arr = [
      {
        name: file?.name,
        file: '',
        type: file?.type.split('/')[1],
        size: file.size,
        isUpload: false,
      },
    ];

    if (file) {
      const reader = new FileReader();
      handleUpload(file, arr);
      reader.readAsDataURL(file);
    }
  };

  const handleUpload = async (file, docs) => {
    try {
      const formData = new FormData();
      formData.append('image', file);
      const { data } = await instance.post('/system/uploadImage', formData, {
        onUploadProgress: (progressEvent) => {
          const percentCompleted = Math.round(
            (progressEvent.loaded * 100) / progressEvent.total
          );
          console.log(`Upload progress: ${percentCompleted}%`);
        },
      });
      if (data) {
        docs[0].isUpload = true;
        docs[0].file = data?.data?.nations;
        setUploadedImage(data?.data?.path);
        setLoader(false);
      }
    } catch (error) {
      ErrorToaster(error);
    }
  };

  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
  } = useForm({
    defaultValues: {
      title: '',
      short_description: '',
      content: '',
      image: '',
    },
  });

  const onSubmit = async (formData) => {
    try {
      let obj = {
        title: formData?.title,
        short_description: formData?.short_description,
        content: formData?.content,
        image: uploadedImage,
      };
      const response = await OrderServices.addBlog(obj);
      if (response?.responseCode === 200) {
        navigate('/blogs');
        SuccessToaster(response?.message);
      }
    } catch (error) {
      ErrorToaster(error);
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #f9fafc, #eef2f7)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        py: 6,
      }}
    >
      <Card
        sx={{
          width: '95%',
          maxWidth: '900px',
          boxShadow: '0 8px 30px rgba(0,0,0,0.08)',
          borderRadius: '16px',
          p: 3,
          backgroundColor: '#fff',
        }}
      >
        <CardContent>
          <Typography
            variant="h4"
            textAlign="center"
            fontWeight="600"
            mb={3}
            sx={{ color: '#1A237E' }}
          >
             Create New Blog
          </Typography>

          <form onSubmit={handleSubmit(onSubmit)}>
            <Grid container spacing={3}>
              {/* Title */}
              <Grid item xs={12}>
                <TextField
                  label="Title"
                  fullWidth
                  variant="outlined"
                  {...register('title', { required: 'Title is required' })}
                  error={!!errors.title}
                  helperText={errors.title?.message}
                />
              </Grid>

              {/* Short Description */}
              <Grid item xs={12}>
                <TextField
                  label="Short Description"
                  fullWidth
                  variant="outlined"
                  {...register('short_description', {
                    required: 'Short Description is required',
                  })}
                  error={!!errors.short_description}
                  helperText={errors.short_description?.message}
                />
              </Grid>

              {/* Content */}
              <Grid item xs={12}>
                <TextField
                  label="Content"
                  variant="outlined"
                  fullWidth
                  multiline
                  rows={6}
                  {...register('content', { required: 'Content is required' })}
                  error={!!errors.content}
                  helperText={errors.content?.message}
                />
              </Grid>

              {/* Image Upload */}
              <Grid item xs={12}>
                <Typography
                  variant="subtitle1"
                  fontWeight={500}
                  mb={1}
                  sx={{ color: '#424242' }}
                >
                  Blog Image
                </Typography>
                <Box
                  sx={{
                    position: 'relative',
                    width: '100%',
                    height: '250px',
                    border: '2px dashed #b0bec5',
                    borderRadius: '12px',
                    overflow: 'hidden',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    backgroundColor: uploadedImage ? '#fafafa' : '#f5f7fa',
                    cursor: 'pointer',
                    transition: '0.3s',
                    '&:hover': {
                      borderColor: '#1976d2',
                      backgroundColor: '#f0f7ff',
                    },
                  }}
                >
                  {uploadedImage ? (
                    <img
                      src={uploadedImage}
                      alt="Uploaded"
                      style={{
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover',
                        borderRadius: '12px',
                      }}
                    />
                  ) : (
                    <Typography
                      variant="body2"
                      color="textSecondary"
                      textAlign="center"
                    >
                      Click below to upload an image
                    </Typography>
                  )}

                  <input
                    accept="image/*"
                    type="file"
                    {...register('image', {
                      required: uploadedImage ? false : 'Image is required',
                    })}
                    onChange={(event) => {
                      handleImageUpload(event);
                      setValue('image', event.target.files);
                    }}
                    style={{ display: 'none' }}
                    id="upload-button-input"
                  />
                  <label
                    htmlFor="upload-button-input"
                    style={{
                      position: 'absolute',
                      bottom: '10px',
                      right: '10px',
                      backgroundColor: '#1976d2',
                      color: 'white',
                      borderRadius: '50%',
                      width: '45px',
                      height: '45px',
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                      cursor: 'pointer',
                      boxShadow: '0 4px 10px rgba(0,0,0,0.2)',
                    }}
                  >
                    {Loader ? <CircularProgress size={24} color="inherit" /> : <UploadIcon />}
                  </label>
                </Box>

                {errors.image && (
                  <FormHelperText error>{errors.image.message}</FormHelperText>
                )}
              </Grid>

              {/* Submit */}
              <Grid item xs={12} display="flex" justifyContent="center">
                <Button
                  variant="contained"
                  color="primary"
                  type="submit"
                  sx={{
                    px: 5,
                    py: 1.2,
                    borderRadius: '10px',
                    fontSize: '16px',
                    textTransform: 'none',
                    boxShadow: '0 4px 14px rgba(25,118,210,0.3)',
                    '&:hover': {
                      boxShadow: '0 6px 20px rgba(25,118,210,0.5)',
                    },
                  }}
                >
                  {loading ? <CircularProgress size={24} color="inherit" /> : 'Publish Blog'}
                </Button>
              </Grid>
            </Grid>
          </form>
        </CardContent>
      </Card>
    </Box>
  );
};

export default CreateBlog;
